import * as React from "react";
import { Modal, Tabs } from "antd";
import { v4 as uuidv4 } from "uuid";
import { Port } from "./port";
import { getDefaultDomainPortInput } from "../utils/utils";
import { PlusOutlined } from "@ant-design/icons";
import { NoPort } from "./noPort";
import { formSetValue, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { cloneDeep } from "lodash";
import { TabLabel } from "../../../workload/draft/tabLabel";
import { PortInput } from "../../../../schema/types/workload/input";
import { useCanRenderAdvanced } from "./useCanRenderAdvanced";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";

type TargetKey = React.MouseEvent | React.KeyboardEvent | string;

const suggestedPorts = [443, 80, 514, 1433, 1443, 3000, 3001, 3002, 3003, 3004, 5912, 8443, 8444, 50051, 50052, 5912];

interface Props {}
export const Ports: React.FC<Props> = () => {
  const { id } = useCpFormContext();

  const canRender = useCanRenderAdvanced();
  const routingMode = useCpFormValue("advanced.spec.routingMode");

  const gvcLink = useCpFormValue("gvc.link");
  const workloadLink = useCpFormValue("advanced.spec.workload.link");

  const ports = useCpFormValue("advanced.spec.ports");
  const activeKey = useCpFormValue("portActiveKey") || ports[0]?.id || "";

  function onEdit(targetKey: React.MouseEvent | React.KeyboardEvent | string, action: "add" | "remove") {
    if (action === "add") {
      add();
    } else {
      Modal.confirm({
        title: "Remove Port",
        content: `Configuration that was made on this port will be lost`,
        onOk() {
          remove(targetKey);
        },
      });
    }
  }

  function add() {
    const newActiveKey = uuidv4();
    const usedPorts = ports.map((p) => p.number);
    let suggestedPort: string | number | undefined = undefined;

    for (const _suggestedPort of suggestedPorts) {
      if (!usedPorts.includes(String(_suggestedPort))) {
        suggestedPort = _suggestedPort;
        break;
      }
    }
    if (!suggestedPort) {
      suggestedPort = usedPorts.toSorted().toReversed()[0] + 1;
    }

    const _ports = cloneDeep(ports);
    _ports.push({
      ...getDefaultDomainPortInput(),
      id: newActiveKey,
      number: String(suggestedPort),
    });
    formSetValue(id, `advanced.spec.ports`, _ports);
  }

  function remove(targetKey: TargetKey) {
    let newActiveKey = activeKey;
    let lastIndex = -1;
    let removeIndex = -1;

    ports.forEach((item, i) => {
      if (item.id === targetKey) {
        lastIndex = i - 1;
        removeIndex = i;
      }
    });

    if (removeIndex >= 0) {
      const _ports: PortInput[] = cloneDeep(ports);
      _ports.splice(removeIndex, 1);
      formSetValue(id, "advanced.spec.ports", _ports);

      if (_ports.length > 1 && newActiveKey === targetKey) {
        if (lastIndex >= 0) {
          newActiveKey = _ports[lastIndex].id!;
        } else {
          newActiveKey = _ports[removeIndex + 1]?.id || _ports[0]?.id || "";
        }
        formSetValue(id, "portActiveKey", newActiveKey);
      }
    }
  }

  function onChange(newActiveKey: string) {
    formSetValue(id, "portActiveKey", newActiveKey);
  }

  if (routingMode === "none") {
    return null;
  }

  if (routingMode === "workloadSubdomain" && (!gvcLink || !workloadLink)) {
    return null;
  }

  if (!canRender) {
    return null;
  }

  return (
    <>
      <div className="cpln-form-section cpln-ant-tabs-container" data-section-id={"ports"}>
        <Tabs
          type={"editable-card"}
          items={ports.map((p, i) => {
            const label = `${p?.number} ${p?.protocol?.toUpperCase()}`;

            return {
              key: p.id!,
              label: <TabLabel label={label} names={[`advanced.spec.ports.${i}`]} />,
              closable: ports.length > 1,
              children: <Port index={i} />,
            };
          })}
          onChange={onChange}
          activeKey={activeKey}
          onEdit={onEdit}
          addIcon={
            <div className="flex items-center gap-2 px-4">
              <div>Add</div>
              <PlusOutlined />
            </div>
          }
        />
        {ports.length < 1 ? <NoPort /> : null}
      </div>
    </>
  );
};
